// src/pages/income/env/disinfection/StatementForm.jsx
import React, { useState, useEffect, useRef } from "react";
import styles from "./StatementForm.module.css";
import api from "../../../../api";

export default function StatementForm() {
  // ✅ 기본 상태값
  const [category] = useState("환경");
  const [subcategory, setSubcategory] = useState("소독방역");
  const [statementDate, setStatementDate] = useState(() => toISO(new Date()));
  const [desc, setDesc] = useState("");

  // ✅ 거래처 자동완성 상태
  const [vendors, setVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState("");
  const [vendorId, setVendorId] = useState(null);
  const [showVendorList, setShowVendorList] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const itemRefs = useRef([]);

  // ✅ highlightIndex가 바뀔 때 자동 스크롤 이동
  useEffect(() => {
    if (highlightIndex >= 0 && itemRefs.current[highlightIndex]) {
      itemRefs.current[highlightIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightIndex]);

  // ✅ 품목 상태
  const [items, setItems] = useState([
    { name: "", qty: 1, unitPrice: 0, supply: 0, vat: 0, total: 0 },
  ]);

  // ✅ 거래처 목록 불러오기
  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        const res = await api.get("/vendors/");
        const data = Array.isArray(res.data)
          ? res.data
          : res.data.results || [];
        setVendors(data);
      } catch (err) {
        console.error("❌ 거래처 목록 불러오기 실패:", err);
      } finally {
        setLoadingVendors(false);
      }
    };
    fetchVendors();
  }, []);

  // ✅ 입력 시 필터링
  const filteredVendors = vendors.filter((v) =>
    v.name.toLowerCase().includes(vendorInput.toLowerCase())
  );

  // ✅ 거래처 입력 핸들러
  const handleChangeVendor = (e) => {
    setVendorInput(e.target.value);
    setVendorId(null);
    setShowVendorList(true);
    setHighlightIndex(-1);
  };

  // ✅ 거래처 선택
  const handlePickVendor = (v) => {
    setVendorId(v.id);
    setVendorInput(v.name);
    setShowVendorList(false);
    setHighlightIndex(-1);
  };

  // ✅ 키보드 이동
  const handleVendorKeyDown = (e) => {
    if (!showVendorList || filteredVendors.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredVendors.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredVendors.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredVendors[highlightIndex]) {
        handlePickVendor(filteredVendors[highlightIndex]);
      }
    }
  };

  // ✅ 품목 추가
  const addItem = () => {
    setItems([
      ...items,
      { name: "", qty: 1, unitPrice: 0, supply: 0, vat: 0, total: 0 },
    ]);
  };

  // ✅ 품목 삭제
  const removeItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(
      newItems.length
        ? newItems
        : [{ name: "", qty: 1, unitPrice: 0, supply: 0, vat: 0, total: 0 }]
    );
  };

  // ✅ 품목 변경
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    if (["qty", "unitPrice"].includes(field)) {
      const qty = parseFloat(newItems[index].qty) || 0;
      const unitPrice = parseFloat(newItems[index].unitPrice) || 0;
      const supply = qty * unitPrice;
      const vat = Math.round(supply * 0.1);
      const total = supply + vat;
      Object.assign(newItems[index], { supply, vat, total });
    }

    setItems(newItems);
  };

  // ✅ 저장
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log("📦 거래명세서 저장 데이터:", {
        vendorInput,
        statementDate,
        items,
        desc,
      });
      alert("거래명세서가 임시 저장되었습니다 (디버깅 모드)");
    } catch (err) {
      console.error("❌ 거래명세서 저장 실패:", err);
      alert("거래명세서 저장 중 오류가 발생했습니다.");
    }
  };

  // ✅ JSX 반환
  return (
    <div className={`${styles.pane} ${styles.statementPane}`}>
      <h3>거래명세서 작성</h3>
      <form onSubmit={handleSubmit} className={styles.form}>
        {/* 거래처명 */}
        <div className={styles.row}>
          <label className={styles.rowLabel}>거래처명</label>
          <div className={styles.partnerRow}>
            <input
              type="text"
              placeholder="직접 입력 또는 검색"
              value={vendorInput}
              onChange={handleChangeVendor}
              onKeyDown={handleVendorKeyDown}
            />
            <button
              type="button"
              className={styles.partnerButton}
              onClick={() => setShowVendorList((v) => !v)}
            >
              ▼
            </button>

            {showVendorList && (
              <div className={styles.partnerBox}>
                {loadingVendors ? (
                  <div>불러오는 중...</div>
                ) : filteredVendors.length > 0 ? (
                  filteredVendors.map((v, i) => (
                    <div
                      key={v.id}
                      ref={(el) => (itemRefs.current[i] = el)}
                      className={`${styles.partnerItem} ${
                        i === highlightIndex ? styles.activeItem : ""
                      }`}
                      onClick={() => handlePickVendor(v)}
                    >
                      {v.name}
                    </div>
                  ))
                ) : (
                  <div>일치하는 거래처가 없습니다.</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 작성일자 */}
        <div className={styles.row}>
          <label className={styles.rowLabel}>작성일자</label>
          <input
            type="date"
            value={statementDate}
            onChange={(e) => setStatementDate(e.target.value)}
            required
          />
        </div>

        {/* ✅ 품목 테이블 (가로 스크롤 지원) */}
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>품목명</th>
                <th>수량</th>
                <th>단가</th>
                <th>공급가액</th>
                <th>부가세</th>
                <th>합계금액</th>
                <th>삭제</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i}>
                  <td>
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) =>
                        handleItemChange(i, "name", e.target.value)
                      }
                      placeholder="품목명"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.qty}
                      onChange={(e) =>
                        handleItemChange(i, "qty", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) =>
                        handleItemChange(i, "unitPrice", e.target.value)
                      }
                    />
                  </td>
                  <td>{item.supply.toLocaleString()}</td>
                  <td>{item.vat.toLocaleString()}</td>
                  <td>{item.total.toLocaleString()}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => removeItem(i)}
                      className={styles.deleteBtn}
                    >
                      ❌
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button type="button" className={styles.addBtn} onClick={addItem}>
          + 품목 추가
        </button>

        <div className={styles.row}>
          <label>비고</label>
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="추가 메모나 비고사항"
          />
        </div>

        <button type="submit" className={styles.submitBtn}>
          거래명세서 저장
        </button>
      </form>
    </div>
  );
}

// ✅ 날짜 포맷 함수
function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
